// Query options of the agency console (M5-05): the operator's approvals SLA, supervisor load,
// cost-to-serve and break-glass sessions, and a company's break-glass sessions (the shell's banner
// and Setup › Break-glass share one cached read). Keys nest under the existing roots
// (`["operator", …]`, `["company", co, …]`) so the usual invalidations reach them.
import type { OperationOutput } from "@companyos/sdk";
import { queryOptions } from "@tanstack/react-query";
import type { Api } from "../../api/client";

export type BreakGlassSession = OperationOutput<"breakGlass.list">["data"][number];
export type BreakGlassRead = OperationOutput<"breakGlass.reads">["data"][number];
export type ApprovalsSla = OperationOutput<"operator.approvalsSla.get">;
export type SupervisorLoad = OperationOutput<"operator.supervisorLoad.get">;
export type CostToServe = OperationOutput<"operator.costToServe.get">;

/** The console's default decision target (`APPROVAL_SLA_DEFAULT_MINUTES`). */
export const SLA_DEFAULT = 240;
export const SLA_CHOICES = [60, 120, 240, 480, 1440] as const;
/** Sessions per page; the banner reads the newest page (live sessions are the newest). */
export const BREAK_GLASS_PAGE = 50;

export const agencyKeys = {
  approvalsSla: (week: string, sla: number) => ["operator", "approvals-sla", week, sla] as const,
  supervisorLoad: (week: string) => ["operator", "supervisor-load", week] as const,
  costToServe: (month: string) => ["operator", "cost-to-serve", month] as const,
  operatorBreakGlass: () => ["operator", "break-glass"] as const,
  companyBreakGlassAll: (co: string) => ["company", co, "break-glass"] as const,
  companyBreakGlass: (co: string, cursor: string | null) =>
    ["company", co, "break-glass", cursor ?? "first"] as const,
  companyBreakGlassReads: (co: string, cursor: string | null) =>
    ["company", co, "break-glass", "reads", cursor ?? "first"] as const,
};

export const approvalsSlaQuery = (api: Api, week: string, sla: number) =>
  queryOptions({
    queryKey: agencyKeys.approvalsSla(week, sla),
    queryFn: () => api.client.operator.approvalsSla.get({ query: { week, sla_minutes: sla } }),
  });

export const supervisorLoadQuery = (api: Api, week: string) =>
  queryOptions({
    queryKey: agencyKeys.supervisorLoad(week),
    queryFn: () => api.client.operator.supervisorLoad.get({ query: { week } }),
  });

export const costToServeQuery = (api: Api, month: string) =>
  queryOptions({
    queryKey: agencyKeys.costToServe(month),
    queryFn: () => api.client.operator.costToServe.get({ query: { month } }),
  });

export const operatorBreakGlassQuery = (api: Api) =>
  queryOptions({
    queryKey: agencyKeys.operatorBreakGlass(),
    queryFn: () => api.client.operator.breakGlass.list({ query: { limit: BREAK_GLASS_PAGE } }),
  });

/** One page of a company's sessions, newest first; the first page is also the banner's read. */
export const companyBreakGlassQuery = (api: Api, co: string, cursor: string | null = null) =>
  queryOptions({
    queryKey: agencyKeys.companyBreakGlass(co, cursor),
    queryFn: () =>
      api.client.breakGlass.list({
        params: { co },
        query: { limit: BREAK_GLASS_PAGE, ...(cursor ? { cursor } : {}) },
      }),
  });

/** BREAK-GLASS: one page of the company's read log, newest first (owners and admins). */
export const companyBreakGlassReadsQuery = (api: Api, co: string, cursor: string | null = null) =>
  queryOptions({
    queryKey: agencyKeys.companyBreakGlassReads(co, cursor),
    queryFn: () =>
      api.client.breakGlass.reads({
        params: { co },
        query: { limit: BREAK_GLASS_PAGE, ...(cursor ? { cursor } : {}) },
      }),
  });

/** `YYYY-MM` of a date (local calendar). */
export function monthKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export function recentMonths(today: Date, count = 12): string[] {
  return Array.from({ length: count }, (_, i) =>
    monthKey(new Date(today.getFullYear(), today.getMonth() - i, 1)),
  );
}

export function monthLabel(key: string): string {
  const [y, m] = key.split("-").map(Number);
  return new Date(y ?? 2000, (m ?? 1) - 1, 1).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
}

/** Minutes → "h:mm" ("7:05", "0:45"). */
export function hMm(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, "0")}`;
}

/** Minutes → "45 min", "5 h 10 min", "2 d 3 h" (ages and decision times). */
export function ageText(minutes: number | null): string {
  if (minutes === null) return "—";
  const m = Math.round(minutes);
  if (m < 60) return `${m} min`;
  if (m < 48 * 60) {
    const h = Math.floor(m / 60);
    const rest = m % 60;
    return rest ? `${h} h ${rest} min` : `${h} h`;
  }
  const d = Math.floor(m / 1440);
  const h = Math.floor((m % 1440) / 60);
  return h ? `${d} d ${h} h` : `${d} d`;
}

/** The SLA target as a label ("4 h", "24 h"). */
export const slaLabel = (minutes: number) =>
  minutes % 60 === 0 ? `${minutes / 60} h` : `${minutes} min`;
